import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, Chip,
  IconButton, Tooltip, InputAdornment,
} from '@mui/material';
import { Add, Delete, ToggleOn, ToggleOff, LocalOffer, ContentCopy } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK') : '—';

function CreateVoucherDialog({ open, onClose, onCreated }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    code: '', type: 'fixed', amount: '', min_purchase: '', usage_limit: '', expires_at: '', description: '', max_discount: '',
  });
  const [saving, setSaving] = useState(false);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    setForm(f => ({ ...f, code: Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('') }));
  };

  const handleSave = async () => {
    if (!form.code || !form.amount) return;
    setSaving(true);
    try {
      await api.post('/vouchers', {
        code: form.code,
        type: form.type,
        amount: +form.amount,
        min_purchase: form.min_purchase ? +form.min_purchase : undefined,
        usage_limit: form.usage_limit ? +form.usage_limit : undefined,
        expires_at: form.expires_at || undefined,
        description: form.description || undefined,
        max_discount: form.max_discount ? +form.max_discount : undefined,
      });
      dispatch(showSnackbar({ message: 'Voucher created!', severity: 'success' }));
      onCreated();
      onClose();
      setForm({ code: '', type: 'fixed', amount: '', min_purchase: '', usage_limit: '', expires_at: '', description: '', max_discount: '' });
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Failed', severity: 'error' }));
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Discount Voucher</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Voucher Code *" value={form.code} onChange={set('code')}
            sx={{ flex: 1 }} inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: 2 } }}
            onBlur={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
          />
          <Button variant="outlined" onClick={generateCode} sx={{ whiteSpace: 'nowrap' }}>Generate</Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select value={form.type} onChange={set('type')} label="Type">
              <MenuItem value="fixed">Fixed ₨</MenuItem>
              <MenuItem value="percent">Percent %</MenuItem>
            </Select>
          </FormControl>
          <TextField label={form.type === 'fixed' ? 'Amount (₨) *' : 'Discount (%) *'} type="number"
            value={form.amount} onChange={set('amount')} sx={{ flex: 1 }} size="small" />
          {form.type === 'percent' && (
            <TextField label="Max Discount ₨" type="number" value={form.max_discount}
              onChange={set('max_discount')} sx={{ flex: 1 }} size="small" />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Min Purchase ₨" type="number" value={form.min_purchase}
            onChange={set('min_purchase')} size="small" sx={{ flex: 1 }} />
          <TextField label="Usage Limit" type="number" value={form.usage_limit}
            onChange={set('usage_limit')} size="small" sx={{ flex: 1 }} />
        </Box>

        <TextField label="Expires On" type="date" value={form.expires_at}
          onChange={set('expires_at')} size="small" InputLabelProps={{ shrink: true }} />

        <TextField label="Description / Note" value={form.description}
          onChange={set('description')} size="small" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !form.code || !form.amount}>
          {saving ? 'Creating…' : 'Create Voucher'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function VouchersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [vouchers, setVouchers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/vouchers', { params: { page, limit: 50 } });
    setVouchers(data.data || []);
    setTotal(data.pagination?.total || 0);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    await api.patch(`/vouchers/${id}/toggle`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this voucher?')) return;
    await api.delete(`/vouchers/${id}`);
    dispatch(showSnackbar({ message: 'Deleted', severity: 'info' }));
    load();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    dispatch(showSnackbar({ message: `Copied: ${code}`, severity: 'success' }));
  };

  const isExpired = (v) => v.expires_at && new Date(v.expires_at) < new Date();
  const isExhausted = (v) => v.usage_limit && v.used_count >= v.usage_limit;

  const statusChip = (v) => {
    if (!v.is_active) return <Chip label="Inactive" size="small" color="default" />;
    if (isExpired(v)) return <Chip label="Expired" size="small" color="error" />;
    if (isExhausted(v)) return <Chip label="Exhausted" size="small" color="warning" />;
    return <Chip label="Active" size="small" color="success" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LocalOffer color="primary" />
          <Typography variant="h5" fontWeight={700}>Discount Vouchers / کوپن</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Create Voucher
        </Button>
      </Box>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip label={`${total} total vouchers`} color="primary" variant="outlined" />
        <Chip label={`${vouchers.filter(v => v.is_active && !isExpired(v) && !isExhausted(v)).length} active`} color="success" variant="outlined" />
        <Chip label={`${vouchers.reduce((s, v) => s + (v.used_count || 0), 0)} total uses`} color="info" variant="outlined" />
      </Box>

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Min Purchase</TableCell>
              <TableCell align="center">Used / Limit</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vouchers.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No vouchers yet. Create one to get started.
                </TableCell>
              </TableRow>
            )}
            {vouchers.map(v => (
              <TableRow key={v.id} hover sx={{ opacity: (!v.is_active || isExpired(v)) ? 0.6 : 1 }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography fontFamily="monospace" fontWeight={700} color="primary">{v.code}</Typography>
                    <Tooltip title="Copy code">
                      <IconButton size="small" onClick={() => copyCode(v.code)}><ContentCopy fontSize="inherit" /></IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={v.type === 'fixed' ? 'Fixed ₨' : 'Percent %'} size="small"
                    color={v.type === 'fixed' ? 'info' : 'secondary'} variant="outlined" />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {v.type === 'fixed' ? `₨ ${fmt(v.amount)}` : `${v.amount}%`}
                  {v.max_discount ? <Typography variant="caption" color="text.secondary" display="block">max ₨{fmt(v.max_discount)}</Typography> : null}
                </TableCell>
                <TableCell align="right">{v.min_purchase > 0 ? `₨ ${fmt(v.min_purchase)}` : '—'}</TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{v.used_count} / {v.usage_limit || '∞'}</Typography>
                </TableCell>
                <TableCell>{fmtDate(v.expires_at)}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>{v.description || '—'}</Typography>
                </TableCell>
                <TableCell>{statusChip(v)}</TableCell>
                <TableCell align="center">
                  <Tooltip title={v.is_active ? 'Deactivate' : 'Activate'}>
                    <IconButton size="small" onClick={() => toggle(v.id)} color={v.is_active ? 'success' : 'default'}>
                      {v.is_active ? <ToggleOn /> : <ToggleOff />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => remove(v.id)}><Delete /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <CreateVoucherDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
    </Box>
  );
}
